'use client';

import { AnalyticsFeatureViewed as FeatureViewed } from '@cc-shopping/platform-analytics/AnalyticsFeatureViewed';
import { usePageAction } from '@cc-shopping/platform-analytics/hooks/usePageAction';
import { theme } from '@cc-shopping/ui/theme';
import { datadogRum } from '@datadog/browser-rum';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import type { StructuredContentDocument } from '../../hydration/types';
import type { Thumbnails } from '../../hydration/Video/types';
import { PlayMediaIcon } from './components/PlayMediaIcon';
import type { VideoSCProps } from './types';
import { getWebPImageUrl, IFRAME_URL_MAPPER } from './utils';

// Add YouTube IFrame API type
declare global {
  interface Window {
    YT?: {
      Player: new (elementId: string, config: {
        videoId: string;
        events: {
          onReady: () => void;
          onStateChange: (event: {
            data: number;
          }) => void;
        };
      }) => void;
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}
type VideoProps = StructuredContentDocument<VideoSCProps>;
export function Video({
  props: {
    options,
    service,
    video
  }
}: VideoProps) {
  const [showPreview, setShowPreview] = useState(options.showPreview ?? true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const impressionId = useRef<string>(uuid());
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    youtubeVideo: {
      id,
      snippet: {
        title,
        thumbnails
      }
    }
  } = video;
  const url = IFRAME_URL_MAPPER[service](id, options);
  const previewImageSrc = thumbnails ? getWebPImageUrl(thumbnails.standard.url) : `https://i.ytimg.com/vi_webp/${id}/mqdefault.webp`;
  let timeLength = options.timeLength;
  if (!timeLength?.match(/^(([0]?[0-5][0-9]|[0-9]):([0-5][0-9]))$/)) {
    timeLength = undefined;
  }
  const firePlayPageAction = usePageAction({
    actionName: 'video',
    actionSection: 'review',
    actionType: 'press',
    actionInput: 'play',
    parentActivityId: impressionId.current
  });
  const firePausePageAction = usePageAction({
    actionName: 'video',
    actionSection: 'review',
    actionType: 'press',
    actionInput: 'pause',
    parentActivityId: impressionId.current
  });
  const fireEndPageAction = usePageAction({
    actionName: 'video',
    actionSection: 'review',
    actionType: 'press',
    actionInput: 'end',
    parentActivityId: impressionId.current
  });
  useEffect(() => {
    // Load YouTube IFrame API if not already loaded
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
    }

    // Create player instance when API is ready
    const createPlayer = () => {
      if (!iframeRef.current?.id) {
        iframeRef.current?.setAttribute('id', `youtube-player-${id}`);
      }
      try {
        if (!window.YT?.Player) {
          throw new Error('YouTube IFrame API not loaded');
        }
        new window.YT.Player(`youtube-player-${id}`, {
          videoId: id,
          events: {
            onReady: () => {
              // Player is ready
            },
            onStateChange: event => {
              switch (event.data) {
                case 1:
                  // playing
                  firePlayPageAction();
                  break;
                case 2:
                  // paused
                  firePausePageAction();
                  break;
                case 0:
                  // ended
                  fireEndPageAction();
                  break;
              }
            }
          }
        });
      } catch (e) {
        datadogRum.addError(e, {
          name: 'YouTubePlayerInitError',
          source: 'Video',
          message: 'Error initializing YouTube player',
          context: {
            videoId: id,
            service
          }
        });
      }
    };

    // Initialize player when API is ready
    if (window.YT?.Player) {
      createPlayer();
    } else {
      window.onYouTubeIframeAPIReady = createPlayer;
    }
    return () => {
      // Cleanup
      if (window.onYouTubeIframeAPIReady === createPlayer) {
        window.onYouTubeIframeAPIReady = undefined;
      }
    };
  }, [id, service, firePlayPageAction, firePausePageAction, fireEndPageAction]);
  const playButtonClickHandler = () => {
    setShowPreview(false);
  };
  const handleKeyDown = (event_0: React.KeyboardEvent) => {
    if (event_0.key === 'Enter' || event_0.key === ' ') {
      event_0.preventDefault(); // Prevent page scroll on space
      playButtonClickHandler();
    }
  };
  const buildSrcSet = (thumbnails_0: NonNullable<Thumbnails>): string => {
    const defaultURL = getWebPImageUrl(thumbnails_0.default.url);
    const mediumURL = getWebPImageUrl(thumbnails_0.medium.url);
    const highURL = getWebPImageUrl(thumbnails_0.high.url);
    const standardURL = getWebPImageUrl(thumbnails_0.standard.url);
    const maxResURL = getWebPImageUrl(thumbnails_0.maxres.url);
    return `${defaultURL} 120w, ${mediumURL} 320w, ${highURL} 480w, ${standardURL} 640w, ${maxResURL} 1280w `;
  };

  // Update the iframe URL to include enablejsapi=1 for postMessage support
  const videoUrl = `${url}&enablejsapi=1`;
  return <FeatureViewed entityName="video" entitySection="review" impressionPosition={0} activityId={impressionId.current}>
      <Box height="100%" maxWidth={options.maxWidth || '100%'} maxHeight={options.maxHeight || '100%'} position="relative" sx={{
      '&::before': {
        content: '""',
        display: 'block',
        paddingTop: '56.25%' // 16:9 aspect ratio
      }
    }}>
        {showPreview ? <Box sx={{
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }} onClick={playButtonClickHandler} onKeyDown={handleKeyDown} role="button" tabIndex={0}>
            <Box sx={{
          backgroundColor: 'white',
          cursor: 'pointer',
          position: 'absolute',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: {
            xs: '70px',
            sm: '96px'
          },
          height: {
            xs: '70px',
            sm: '96px'
          },
          borderRadius: '100%',
          opacity: 0.9,
          zIndex: 1,
          border: 0
        }} aria-label="Play" role="button">
              <PlayMediaIcon color="#005fb9" size={isMobile ? 'x-large' : 'xx-large'} style={{
            marginLeft: '4px',
            marginTop: isMobile ? '3px' : '2px'
          }} />
            </Box>
            {timeLength && <Box sx={{
          backgroundColor: 'black',
          color: 'white',
          justifyContent: 'center',
          paddingY: '0.5rem',
          paddingX: '1rem',
          position: 'absolute',
          textAlign: 'center',
          width: 'auto',
          height: 'auto',
          bottom: {
            xs: 2,
            sm: 3
          },
          right: {
            xs: 2,
            sm: 3
          },
          zIndex: '1'
        }}>
                <Typography>{timeLength}</Typography>
              </Box>}
            {/* biome-ignore lint/performance/noImgElement: for backwards compatibility we kept legacy image HTML component */}
            <img alt="Video preview image" loading="lazy" width="100%" height="100%" src={previewImageSrc} {...thumbnails && {
          srcSet: buildSrcSet(thumbnails)
        }} />
          </Box> : <iframe id={`youtube-player-${id}`} title={title ?? options.title} ref={iframeRef} src={videoUrl} width="100%" height="100%" style={{
        border: 0,
        borderWidth: '0px',
        maxWidth: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }} allow="autoplay" allowFullScreen />}
      </Box>
    </FeatureViewed>;
}